import type { SerializedData } from 'bernie-core';
import { timeoutPromise } from 'bernie-core';
import type { Request } from 'bernie-http';
import type { Logger } from 'bernie-logger';
import { NOOP_LOGGER } from 'bernie-logger';
import { Store } from 'bernie-plugin-mobx';

import {
  FETCHING_TEMPLATE,
  TEMPLATE_CACHE_ENTRY_EXPIRED,
  TEMPLATE_CACHE_HIT,
  TEMPLATE_CACHE_MISS,
  TEMPLATE_FETCH_FAILED,
  TEMPLATE_FETCH_SUCCESS,
  TEMPLATE_MISSING,
  TEMPLATE_SET_IN_CACHE
} from 'logging/log-events';
import { isLoggableError, logDetailedEvent } from 'logging/logger';

import type { TemplateName } from 'constants/template-name';
import type { ExperienceTemplate } from 'experience-template-renderer-react';
import fetch from 'isomorphic-fetch';

interface CacheEntry {
  template: ExperienceTemplate;
  timestamp: number;
}

export class TemplateStore extends Store {
  public templateApiServerTimeout: number;
  public templateApiBrowserTimeout: number;
  public templateApiPath: string;
  public templateApiOrigin: string;
  public isServer: boolean;

  private locale: string;
  private headers: Request['headers'];

  private static TEMPLATE_CACHE_TTL_IN_MINUTES = 15;

  private templateCache: Map<string, CacheEntry> = new Map();

  constructor(
    state: SerializedData = {},
    logger: Logger = NOOP_LOGGER,
    locale: string,
    headers: Request['headers'],
    isServer: boolean
  ) {
    super({}, logger);

    this.templateApiPath = state.path;
    this.templateApiOrigin = state.origin;
    this.templateApiServerTimeout = state.serverTimeout;
    this.templateApiBrowserTimeout = state.browserTimeout;
    this.locale = locale;
    this.headers = headers;
    this.isServer = isServer;
  }

  private get timeout() {
    return this.isServer ? this.templateApiServerTimeout : this.templateApiBrowserTimeout;
  }

  hydrate(data: SerializedData): void {
    const { templateCache, ...templateStoreProperties } = data;

    Object.assign(this, templateStoreProperties);
    this.templateCache = new Map(templateCache);
  }

  public getCachedTemplate(cacheKey: string): ExperienceTemplate | undefined {
    const cachedEntry = this.templateCache.get(cacheKey);

    if (!cachedEntry) {
      logDetailedEvent(this.logger, TEMPLATE_CACHE_MISS);

      return undefined;
    }

    const timeElapsedInMinutesSinceTemplateWasCached = (Date.now() - cachedEntry.timestamp) / 1000 / 60;

    if (timeElapsedInMinutesSinceTemplateWasCached < TemplateStore.TEMPLATE_CACHE_TTL_IN_MINUTES) {
      logDetailedEvent(this.logger, TEMPLATE_CACHE_HIT);

      return cachedEntry.template;
    }

    logDetailedEvent(this.logger, TEMPLATE_CACHE_ENTRY_EXPIRED);

    this.templateCache.delete(cacheKey);
    return undefined;
  }

  public setCachedTemplate(cacheKey: TemplateName, templateJson: ExperienceTemplate): void {
    this.templateCache.set(cacheKey, {
      template: templateJson,
      timestamp: Date.now()
    });
  }

  public async fetchTemplate(templateName: TemplateName, uri?: string): Promise<ExperienceTemplate | undefined> {
    try {
      const templateApiUrl = `${this.templateApiOrigin ?? ''}${this.templateApiPath}`;

      const requestBody: RequestInit['body'] = JSON.stringify({
        uri,
        uri_context: { destination: templateName },
        platform_version: { platform: 'WEB', version: '1.0' },
        locale: this.locale
      });

      const requestHeaders: Record<string, string> = {
        ...this.headers,
        'Content-Type': 'application/json'
      };

      logDetailedEvent(this.logger, FETCHING_TEMPLATE, {
        otherMetaData: {
          templateApiRequestBody: requestBody,
          templateApiRequestHeaders: JSON.stringify(requestHeaders),
          templateApiUrl
        }
      });

      const startTime = Date.now();

      const templateApiResponse = await timeoutPromise(
        fetch(templateApiUrl, {
          method: 'POST',
          headers: requestHeaders,
          body: requestBody
        }),
        this.timeout,
        'template api request timeout'
      );

      if (templateApiResponse.status !== 200) {
        throw new Error(
          `template api returned a non 200 status code. status=${templateApiResponse.status} statusText="${templateApiResponse.statusText}"`
        );
      }

      const templateJson = await templateApiResponse.json();

      logDetailedEvent(this.logger, TEMPLATE_FETCH_SUCCESS, {
        otherMetaData: {
          duration: Date.now() - startTime
        }
      });

      if (templateJson) {
        this.setCachedTemplate(templateName, templateJson);
        logDetailedEvent(this.logger, TEMPLATE_SET_IN_CACHE, {
          otherMetaData: {
            headers: this.headers,
            locale: this.locale,
            templateName
          }
        });
      } else {
        logDetailedEvent(this.logger, TEMPLATE_MISSING, {
          otherMetaData: {
            headers: this.headers,
            locale: this.locale,
            templateName
          }
        });
      }

      return templateJson;
    } catch (error) {
      logDetailedEvent(this.logger, TEMPLATE_FETCH_FAILED, {
        message: 'Error while fetching template',
        error: isLoggableError(error) ? error : undefined,
        otherMetaData: {
          headers: this.headers,
          locale: this.locale,
          templateName
        }
      });
    }
  }

  public toJSON(): SerializedData {
    return {
      ...super.toJSON([
        'templateCache',
        'TEMPLATE_CACHE_TTL_IN_MINUTES',
        'locale',
        'headers',
        'templateApiUrl',
        'templateApiOrigin',
        'templateApiServerTimeout',
        'isServer'
      ]),
      templateCache: Array.from(this.templateCache)
    };
  }
}
